/**
 * Idle-timeout hook.
 *
 * Watches for user activity (mousemove, keydown, scroll, touchstart,
 * pointerdown) and tracks inactivity time across tabs via a
 * localStorage heartbeat (so two open tabs both reset on activity in
 * either).
 *
 * Emits two signals:
 *   - onWarn() at WARN_BEFORE_MS before timeout, so the UI can show a
 *     "you'll be logged out" prompt
 *   - onTimeout() when idle for IDLE_TIMEOUT_MS, so the app can call
 *     the logout endpoint and route to /login
 *
 * The server enforces its own idle timeout independently in
 * tokenVerify.js — this hook is the friendly UX layer on top.
 */

import { useEffect, useRef } from 'react';

// Shared with useSessionKeepalive, which decides whether to slide the server
// session by asking "has this user done anything since our last ping?".
export const ACTIVITY_STORAGE_KEY = 'dwms_last_activity';
const STORAGE_KEY = ACTIVITY_STORAGE_KEY;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'pointerdown'];

const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_IDLE_TIMEOUT_MS) || 15 * 60 * 1000;
const DEFAULT_WARN_MS = Number(process.env.REACT_APP_IDLE_WARN_MS) || 2 * 60 * 1000;
const CHECK_INTERVAL_MS = 5 * 1000;

function readLastActivity() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const n = raw ? Number(raw) : 0;
        return Number.isFinite(n) && n > 0 ? n : Date.now();
    } catch {
        return Date.now();
    }
}

function writeLastActivity(ts) {
    try { localStorage.setItem(STORAGE_KEY, String(ts)); } catch { /* private mode */ }
}

export default function useIdleTimeout({
    enabled = true,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    warnBeforeMs = DEFAULT_WARN_MS,
    onWarn,
    onTimeout,
} = {}) {
    const warnedRef = useRef(false);
    const firedRef = useRef(false);

    useEffect(() => {
        if (!enabled) return undefined;

        const bumpActivity = () => {
            writeLastActivity(Date.now());
            warnedRef.current = false;
        };

        // Seed once on mount
        bumpActivity();

        ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, bumpActivity, { passive: true }));

        const onStorage = (e) => {
            // Cross-tab sync: another tab saw activity → clear our warning
            if (e.key === STORAGE_KEY) warnedRef.current = false;
        };
        window.addEventListener('storage', onStorage);

        const tick = setInterval(() => {
            const last = readLastActivity();
            const idleMs = Date.now() - last;
            if (idleMs >= timeoutMs && !firedRef.current) {
                firedRef.current = true;
                onTimeout && onTimeout();
            } else if (idleMs >= timeoutMs - warnBeforeMs && !warnedRef.current && !firedRef.current) {
                warnedRef.current = true;
                onWarn && onWarn({ msUntilTimeout: Math.max(0, timeoutMs - idleMs) });
            }
        }, CHECK_INTERVAL_MS);

        return () => {
            clearInterval(tick);
            ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, bumpActivity));
            window.removeEventListener('storage', onStorage);
        };
    }, [enabled, timeoutMs, warnBeforeMs, onWarn, onTimeout]);
}
